<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
        >
            <div class="d-flex align-items-start">
                <date-range-picker
                    :range-options="predefinedRanges"
                    :value="dateRange"
                    :date-format="userUnicodeDateFormat"
                    @input="handleDaterangeChange"
                />
                <div class="d-none d-lg-flex align-items-baseline mr-1 mb-1 filter-properties">
                    <search-multiselect
                        v-model="filterProperties"
                        :options="filterPropertyOptions"
                        :icon="['fas', 'hotel']"
                        :responsiveness="true"
                        variant="primary"
                        placeholder="Properties"
                    />
                </div>
                <div class="d-none d-lg-flex align-items-baseline mr-1 mb-1 filter-booking-methods">
                    <search-multiselect
                        v-model="filterBookingMethods"
                        :options="filterBookingMethodOptions"
                        :icon="['fas', 'filter']"
                        :responsiveness="true"
                        variant="primary"
                        placeholder="Booking Methods"
                    />
                </div>
                <div class="d-none d-lg-flex align-items-baseline mr-1 mb-1 filter-referrers">
                    <search-multiselect
                        v-model="filterReferrers"
                        :options="filterReferrerOptions"
                        :icon="['fas', 'filter']"
                        :responsiveness="true"
                        variant="primary"
                        placeholder="NextPax Channels"
                    />
                </div>
                <responsive-button
                    :href="exportExcelHref"
                    :icon="['fas', 'file-excel']"
                    @click="generatingExcelReportSuccessToast"
                    variant="primary"
                />
                <exportToPDFButton
                    :href="exportPdfHref"
                    :pdfCacheKey="pdfCacheKey"
                    :pageOrientation="pageOrientation.toLowerCase()"
                />
                <responsive-dropdown :icon="['fas', 'file']" :items="pageOrientationOptions" variant="primary">
                    {{ pageOrientation }}
                </responsive-dropdown>
                <b-button-group class="d-lg-none h-100">
                    <more-filters-dropdown>
                        <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                            <search-multiselect
                                v-model="filterProperties"
                                :options="filterPropertyOptions"
                                :icon="['fas', 'hotel']"
                                variant="primary"
                                placeholder="Properties"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            />
                            <search-multiselect
                                v-model="filterBookingMethods"
                                :options="filterBookingMethodOptions"
                                :icon="['fas', 'filter']"
                                variant="primary"
                                placeholder="Booking Methods"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            />
                            <search-multiselect
                                v-model="filterReferrers"
                                :options="filterReferrerOptions"
                                :icon="['fas', 'filter']"
                                variant="primary"
                                placeholder="NextPax Channels"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            />
                        </template>
                    </more-filters-dropdown>
                </b-button-group>
                <report-settings-modal
                    :modalId="reportSettingsModalId"
                    :initialValues="reportSettings"
                    class="ml-1 ml-md-0"
                />
            </div>
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import SearchMultiselect from '../generic/search-multiselect/search-multiselect.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        TOMORROW,
        THIS_WEEK,
        NEXT_WEEK,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_WEEK,
        LAST_YEAR,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import ReportSettingsModal, {
        DATE_PERIODS_INPUT,
        MULTI_SELECT_INPUT,
    } from './report-settings/report-settings-modal.vue';
    import { REPORT_CANCELLATION_ENDPOINT, REPORT_CANCELLATION_NEXTPAX_REFERRERS_ENDPOINT } from '../../endpoints';
    import ReportSummaryBar from './report-summary-bar.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import { escapeHTML } from '../../utils/strings';
import { defineAsyncComponent } from 'vue';

    const PAGE_ORIENTATION_PORTRAIT = 'Portrait';
    const PAGE_ORIENTATION_LANDSCAPE = 'Landscape';
    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        name: 'ReportCancellation',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            ResponsiveDropdown,
            exportToPDFButton,
            DateRangePicker,
            SearchMultiselect,
            reportSummaryBar: ReportSummaryBar,
            MoreFiltersDropdown,
            ReportSettingsModal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Cancellation Report',
                tabs: [
                    {
                        title: 'Cancellation Report',
                        route: '/app/reports/cancellation/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(THIS_MONTH),
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                gridApi: null,
                rowData: [],
                getRowStyle: null,
                windowWidth: 0,
                windowHeight: 0,
                totalRecords: 0,
                pageOrientation: PAGE_ORIENTATION_LANDSCAPE,
                filterBookingMethods: [],
                filterBookingMethodOptions: [
                    {
                        value: 'manual',
                        text: 'Manual',
                    },
                    {
                        value: 'online',
                        text: 'Online',
                    },
                    {
                        value: 'AIRBNB',
                        text: 'Airbnb',
                    },
                    {
                        value: 'NEXTPAX',
                        text: 'NextPax',
                    },
                    {
                        value: 'HOMEAWAY',
                        text: 'VRBO',
                    },
                    {
                        value: 'FLIPKEY',
                        text: 'TripAdvisor',
                    },
                    {
                        value: 'LODGIXAPI',
                        text: 'Lodgix API',
                    },
                    {
                        value: 'LODGIXAPP',
                        text: 'Lodgix App',
                    },
                ],
                filterProperties: [],
                filterPropertyOptions: [],
                filterReferrers: [],
                filterReferrerOptions: [],
                statusBar: null,
                total: {},
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            pinnedBottomRowData() {
                return [
                    {
                        r_charges: this.total?.r_charges || 0,
                        total_charges: this.total?.total_charges || 0,
                        paid: this.total?.paid || 0,
                        cancellation_fee: this.total?.cancellation_fee || 0,
                    },
                ];
            },
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                        minWidth: 40,
                    },
                    {
                        field: 'timestamp',
                        headerName: 'Timestamp',
                        minWidth: 120,
                        type: 'dateTimeColumn',
                    },
                    {
                        field: 'meta_res_id',
                        headerName: 'Invoice #',
                        minWidth: 80,
                        cellRenderer: ({ data, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }
                            return `<a target="_self" href="/app/reservation/edit/invoice/${data?.meta_res_id}">${data?.meta_res_id}</a>`;
                        },
                    },
                    {
                        field: 'guest',
                        headerName: 'Guest',
                        minWidth: 120,
                        maxWidth: 180,
                    },
                    {
                        field: 'properties',
                        headerName: 'Property/Room',
                        valueGetter: ({ data }) =>
                            data &&
                            (data.properties || []).map((property) => this.shortPropertyName(property)).join('; '),
                        cellRenderer: ({ data }) =>
                            data &&
                            (data.properties || [])
                                .map((property) => escapeHTML(this.shortPropertyName(property)))
                                .join('<br/>'),
                        autoHeight: true,
                        minWidth: 120,
                    },
                    {
                        field: 'from_date',
                        headerName: 'Arrival',
                        type: 'dateColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'to_date',
                        headerName: 'Departure',
                        type: 'dateColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'created',
                        headerName: 'Entered',
                        type: 'dateTimeColumn',
                        minWidth: 120,
                    },
                    {
                        field: 'booking_method',
                        headerName: 'Booking Method',
                        minWidth: 100,
                    },
                    {
                        field: 'note',
                        headerName: 'Note',
                        minWidth: 100,
                    },
                    {
                        field: 'r_charges',
                        headerName: 'Rental Charges',
                        type: 'amountColumn',
                        minWidth: 100,
                        cellClass: 'ag-left-aligned-cell',
                        headerClass: 'ag-left-aligned-header',
                    },
                    {
                        field: 'total_charges',
                        headerName: 'Total Charges',
                        type: 'amountColumn',
                        minWidth: 100,
                        cellClass: 'ag-left-aligned-cell',
                        headerClass: 'ag-left-aligned-header',
                    },
                    {
                        field: 'paid',
                        headerName: 'Paid Sum',
                        type: 'amountColumn',
                        minWidth: 80,
                        cellClass: 'ag-left-aligned-cell',
                        headerClass: 'ag-left-aligned-header',
                    },
                    {
                        field: 'cancellation_fee',
                        headerName: 'Cancellation Fee',
                        type: 'amountColumn',
                        minWidth: 120,
                        cellClass: 'ag-left-aligned-cell',
                        headerClass: 'ag-left-aligned-header',
                        autoWidthExclude: true,
                    },
                ];
            },
            pageOrientationOptions() {
                return [
                    {
                        label: PAGE_ORIENTATION_LANDSCAPE,
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION_LANDSCAPE;
                        },
                    },
                    {
                        label: PAGE_ORIENTATION_PORTRAIT,
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION_PORTRAIT;
                        },
                    },
                ];
            },
            exportPdfHref() {
                let url = '/app/reports/cancellation/row-data/pdf/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.filterProperties.length > 0 ? this.filterProperties.join(',') : ''}`;
                url += `&booking_methods=${
                    this.filterBookingMethods.length > 0 ? this.filterBookingMethods.join(',') : ''
                }`;
                url += `&referrers=${this.filterReferrers.length > 0 ? this.filterReferrers.join(',') : ''}`;
                return url;
            },
            exportExcelHref() {
                let url = '/app/reports/cancellation/row-data/excel/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.filterProperties.length > 0 ? this.filterProperties.join(',') : ''}`;
                url += `&booking_methods=${
                    this.filterBookingMethods.length > 0 ? this.filterBookingMethods.join(',') : ''
                }`;
                url += `&referrers=${this.filterReferrers.length > 0 ? this.filterReferrers.join(',') : ''}`;
                return url;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md || this.windowHeight < md;
            },
            reportSettings() {
                return {
                    name: 'Cancellation Report',
                    report_sysname: 'CANCELLATION',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            label: 'Date Range',
                            name: 'date_range',
                            predefinedRanges: this.predefinedRanges,
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            options: this.filterPropertyOptions,
                            name: 'properties',
                            value: [],
                            label: 'Only for Properties',
                            placeholder: 'All Properties',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            options: this.filterBookingMethodOptions,
                            name: 'booking_methods',
                            value: [],
                            label: 'Booking Methods',
                            placeholder: 'All Booking Methods',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            options: this.filterReferrerOptions,
                            name: 'referrers',
                            value: [],
                            label: 'Referrer',
                            placeholder: 'All Referrers',
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchProperties();
            this.fetchReferrers();
            this.fetchData();
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_CANCELLATION_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            properties: this.filterProperties.length > 0 ? this.filterProperties.join(',') : '',
                            booking_methods:
                                this.filterBookingMethods.length > 0 ? this.filterBookingMethods.join(',') : '',
                            referrers: this.filterReferrers.length > 0 ? this.filterReferrers.join(',') : '',
                        },
                    });
                    const { cancellations, total, pdf_cache_key } = data;
                    this.rowData = [...cancellations.map((c) => ({ ...c, ...c.cost }))];
                    this.pdfCacheKey = pdf_cache_key;
                    this.totalRecords = cancellations.length;
                    this.total = { ...total };
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchProperties() {
                const { data } = await API.get(`/app/reports/cancellation/row-data/properties/`);
                this.filterPropertyOptions = data.map((item) => ({
                    value: !!item.room_id ? -item.room_id : item.property_id,
                    text: item.name,
                    disabled: item.disabled,
                }));
            },
            async fetchReferrers() {
                try {
                    const { data } = await API.get(REPORT_CANCELLATION_NEXTPAX_REFERRERS_ENDPOINT());
                    this.filterReferrerOptions = data.map((referrer) => ({
                        value: referrer,
                        text: referrer,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch NextPax referrers.' });
                }
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
            shortPropertyName(property) {
                const { room, rental_property } = property;
                if (room) {
                    const items = room.split(' — ');
                    return [items[0], '...', items[items.length - 1]].join(' — ');
                }
                return rental_property;
            },
        },
        watch: {
            filterProperties: {
                deep: true,
                handler() {
                    this.fetchData();
                },
            },
            filterBookingMethods: {
                deep: true,
                handler() {
                    this.fetchData();
                },
            },
            filterReferrers: {
                deep: true,
                handler() {
                    this.fetchData();
                },
            },
        },
    };
</script>

<style lang="scss" scoped>
    .filter-properties,
    .filter-booking-methods,
    .filter-referrers {
        > fieldset {
            margin: 0 !important;
        }
    }
</style>
